import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { format } from "date-fns";
import { enUS } from "date-fns/locale";
import jsPDF from "jspdf";
import { useContext, useEffect, useMemo, useRef } from "react";
import { useLocation } from "react-router-dom";
import { commonUserListColumns } from "../../components/Table/CommonColumnsForUserList";
import { calculateUserMedicalStatus } from "../../components/Users/calculateUserMedicalStatus";
import { UserContext } from "../../Context/UserContextAPI";
import "../Investigation/Report/Certificates/HeldUp.scss";
import "../Investigation/Report/Certificates/Unfit.scss";
import "../ProcessAndProcedure/MedicalReports/MedicalReports.scss";
import "./PrintRegistration.scss";

const PrintReadRegistration = () => {
  const pdfRef = useRef();
  const { state } = useLocation();
  const { currentUser, settingsData } = useContext(UserContext);

  const now = new Date();
  const options = {
    day: "numeric",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  };

  const formattedDate = new Intl.DateTimeFormat("en-US", options).format(now);

  const columnHelper = createColumnHelper();

  const commonColumns = useMemo(() => {
    // Move the column filtering logic to a separate function for clarity
    const filterColumns = (columns) =>
      columns.filter(
        (item) =>
          item &&
          item.id !== "amount" &&
          item.id !== "multiple_select" &&
          item.id !== "type"
      );

    // Apply the filter to the common columns
    return filterColumns(
      commonUserListColumns(state?.checkedUsers, {
        serialNumberIncrement: true,
        individualPackageFullName: true,
      })
    );
  }, [state?.checkedUsers]);

  const columns = useMemo(() => [...commonColumns], [commonColumns]);

  const table = useReactTable({
    data: state?.checkedUsers,
    columns: columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const pdfGenerate = () => {
    const input = pdfRef.current;
    const doc = new jsPDF("p", "mm");

    const addHeader = () => {
      let headerDate;
      let headerText; // Default value
      headerText = state?.searchedData?.introducer?.label
        ? state?.searchedData?.introducer?.label
        : "All Registration"; // Reset default value

      const dateFormat = "dd-MMM-yyyy"; // Define the date format
      let fromDate;
      let toDate;
      const fromDateValue = state?.searchedData?.from_date;
      const toDateValue = state?.searchedData?.to_date;

      fromDate = format(new Date(fromDateValue || new Date()), dateFormat, {
        locale: enUS,
      });

      toDate = format(new Date(toDateValue || new Date()), dateFormat, {
        locale: enUS,
      });

      headerDate = `From ${fromDate} To ${toDate}`;

      const fontSize = 9;

      // Set container width to 100%
      const containerWidth = doc.internal.pageSize.width;

      doc.setFontSize(fontSize);

      // Calculate text width for headerText
      const textWidth = doc.getTextWidth(headerText);

      // Center headerText within the container
      const headerTextXCoordinate = (containerWidth - textWidth) / 2;
      doc.text(headerText, headerTextXCoordinate, 10);

      // Calculate text width for headerFromDate
      headerDate = `From ${fromDate} To ${toDate}`;
      const headerFromDate = headerDate; // Assign headerFromDate a value

      const headerFromDateWidth = doc.getTextWidth(headerFromDate);

      // Center headerFromDate within the container
      const headerFromDateXCoordinate =
        (containerWidth - headerFromDateWidth) / 2;

      // Calculate the yCoordinate for centering headerFromDate below headerText
      const headerTextHeight = doc.getTextDimensions(headerText, {
        fontSize,
      }).h;
      const headerFromDateHeight = doc.getTextDimensions(headerFromDate, {
        fontSize,
      }).h;
      const totalHeaderFromDateHeight = headerTextHeight + headerFromDateHeight;
      const yCoordinateFromDate = 10 + totalHeaderFromDateHeight / 2; // Adjust 2 for spacing

      doc.text(headerFromDate, headerFromDateXCoordinate, yCoordinateFromDate);
    };

    const addFooter = () => {
      const xCoordinate = doc.internal.pageSize.width / 2;

      doc.setFontSize(9);

      const userText = `Created at ${formattedDate} by ${currentUser?.f_name}`;
      const userTextWidth = doc.getTextWidth(userText);
      doc.text(
        userText,
        xCoordinate - userTextWidth / 2,
        doc.internal.pageSize.height - 10
      );

      const copyrightText = `Copyright © ${new Date().getFullYear()} ${
        settingsData?.website_name
      }`;
      const copyrightTextWidth = doc.getTextWidth(copyrightText);
      doc.text(
        copyrightText,
        xCoordinate - copyrightTextWidth / 2,
        doc.internal.pageSize.height - 5
      );
    };

    // Adjust the font size and add borders to thead
    const tableOptions = {
      theme: "grid",
      margin: {
        left: 10,
        right: 10,
      },
      styles: {
        fontSize: 8,
        halign: "center",
        valign: "middle",
        cellPadding: 0.5,
        cellWidth: "auto",
      },
      headStyles: {
        fillColor: [200, 200, 200],
        textColor: [0, 0, 0],
        lineColor: [0, 0, 0],
        lineWidth: 0.2,
      },
      bodyStyles: {
        lineColor: [0, 0, 0],
        lineWidth: 0.2,
      },
    };

    // Add the header on the first page
    addHeader();
    addFooter();

    // Add content and footer to each page
    let pageNumber = 1;
    doc.autoTable({
      beforePageContent: function () {
        if (pageNumber > 1) {
          addHeader(); // Add header on every page except the first one
        }
      },
      afterPageContent: function () {
        addFooter(); // Add footer on every page
        pageNumber++;
      },
      html: input,
      startY: 20,
      ...tableOptions,
    });

    // Save the PDF
    doc.save(
      `Registration Report - ${state?.searchedData?.fromDate ?? ""} ${
        state?.searchedData?.toDate ? "To" : ""
      } ${state?.searchedData?.toDate ?? ""}.pdf`
    );
  };

  useEffect(() => {
    if (currentUser?.f_name) {
      setTimeout(() => {
        pdfGenerate();
      }, 1200);
    }
  }, [currentUser]);

  return (
    <>
      <div className="card print-read-registration">
        <div className="card-body">
          <div className="rounded">
            <div className="print-reg-header">
              <div className="card shadow-none mb-3 mt-2">
                <div className="border p-3 rounded">
                  {state?.searchedData?.introducer ? (
                    <h6 className="mb-0 text-uppercase text-center fw-bold">
                      {state?.searchedData?.introducer?.label}{" "}
                    </h6>
                  ) : (
                    <h6 className="mb-0 text-uppercase text-center fw-bold text-dark">
                      All Registration
                    </h6>
                  )}
                  <div className="mb-0 text-center fw-light">
                    From{" "}
                    {state?.searchedData?.fromDate
                      ? format(
                          new Date(state?.searchedData?.fromDate),
                          "dd-MMM-yyyy",
                          { locale: enUS } // or use es for Spanish, adjust as needed
                        )
                      : format(new Date(), "dd-MMM-yyyy", {
                          locale: enUS,
                        })}{" "}
                    To{" "}
                    {state?.searchedData?.toDate
                      ? format(
                          new Date(state?.searchedData?.toDate),
                          "dd-MMM-yyyy",
                          { locale: enUS } // or use es for Spanish, adjust as needed
                        )
                      : format(new Date(), "dd-MMM-yyyy", { locale: enUS })}
                  </div>
                </div>
              </div>
            </div>

            <table
              className="table align-middle table-hover table-bordered mb-0 rounded mb-2"
              ref={pdfRef}
            >
              <thead className="bg-primary">
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header, i) => (
                      <th
                        key={i}
                        id={`th-${i}`}
                        className="text-light p-1"
                        style={{ textAlign: "center", verticalAlign: "middle" }}
                      >
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {table.getRowModel().rows.map((row, i) => {
                  const { isDone, status } = calculateUserMedicalStatus(
                    row?.original
                  );

                  const rowClass = (() => {
                    if (status === 2) {
                      return isDone ? "text-success" : "";
                    } else if (status === 3) {
                      return isDone ? "text-danger" : "";
                    } else if (status === 4) {
                      return isDone ? "text-primary" : "";
                    } else {
                      return "text-dark";
                    }
                  })();
                  return (
                    <tr key={i} className={rowClass}>
                      {row.getVisibleCells().map((cell) => (
                        <td key={cell.id} className="p-1">
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
        <div className="print-reg-footer">
          <div className="d-flex flex-column align-items-center justify-content-center pb-3 text-dark">
            <div>
              Created at {formattedDate} by {currentUser?.f_name}
            </div>
            <div>
              Copyright &copy; {new Date().getFullYear()}{" "}
              {settingsData?.website_name}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default PrintReadRegistration;
